/**
 * Browser Detection Utility
 *
 * Detects in-app browsers (WebViews) that are blocked by Google OAuth.
 * Google rejects OAuth requests from embedded browsers for security reasons
 * (RFC 8252 - OAuth 2.0 for Native Apps).
 *
 * Common in-app browsers: LINE, Instagram, Facebook, Twitter/X, TikTok,
 * WeChat, Snapchat, Pinterest, etc.
 */

/**
 * Known in-app browser identifiers found in user-agent strings.
 * Each entry is a lowercase substring to match against navigator.userAgent.
 */
const IN_APP_BROWSER_SIGNATURES = [
  'line/',          // LINE app
  'liff',           // LINE Front-end Framework
  'fbav',           // Facebook app (Android)
  'fban',           // Facebook app (iOS)
  'fb_iab',         // Facebook in-app browser
  'instagram',      // Instagram app
  'twitter',        // Twitter/X app
  'tiktok',         // TikTok app
  'wechat',         // WeChat
  'micromessenger',  // WeChat (alternative identifier)
  'snapchat',       // Snapchat
  'pinterest',      // Pinterest
  'linkedin',       // LinkedIn app
  'kakaotalk',      // KakaoTalk (popular in Korea)
  'naver',          // Naver app (popular in Japan/Korea)
  'yahoo',          // Yahoo app browser
  'zalo',           // Zalo (popular in Vietnam)
];

/**
 * Detects whether the current browser is an in-app browser (WebView).
 *
 * These browsers are rejected by Google OAuth with error 403: disallowed_useragent.
 * When detected, we should guide users to open the page in their default browser.
 *
 * @returns true if the current browser is an in-app browser
 */
export function isInAppBrowser(): boolean {
  if (typeof navigator === 'undefined') return false;

  const ua = navigator.userAgent.toLowerCase();

  // Check for known in-app browser signatures
  if (IN_APP_BROWSER_SIGNATURES.some((sig) => ua.includes(sig))) {
    return true;
  }

  // Generic WebView detection for Android
  // Android WebViews include "wv" in the user-agent
  if (ua.includes('android') && ua.includes('wv')) {
    return true;
  }

  return false;
}

/**
 * Returns the current page URL suitable for opening in an external browser.
 * Strips any unnecessary query parameters and returns a clean URL.
 */
export function getCurrentPageUrl(): string {
  return window.location.origin + window.location.pathname;
}

/**
 * Attempts to open the given URL in the device's default external browser.
 *
 * Strategy:
 * 1. Android: Use intent:// scheme to launch default browser
 * 2. Fallback: window.open with _system target (works in some WebViews)
 * 3. Final fallback: returns false so caller can show copy-URL UI
 *
 * @param url - The URL to open in an external browser
 * @returns true if the open was attempted (doesn't guarantee success)
 */
export function openInExternalBrowser(url: string): boolean {
  const ua = navigator.userAgent.toLowerCase();

  // Android: Use intent:// scheme to launch default browser
  if (ua.includes('android')) {
    try {
      const intentUrl =
        `intent://${url.replace(/^https?:\/\//, '')}` +
        '#Intent;scheme=https;action=android.intent.action.VIEW;end';
      window.location.href = intentUrl;
      return true;
    } catch {
      // Fall through to fallback
    }
  }

  // iOS and other platforms: Try window.open
  try {
    const opened = window.open(url, '_system');
    if (opened) return true;

    // Some WebViews block _system, try _blank
    const openedBlank = window.open(url, '_blank');
    if (openedBlank) return true;
  } catch {
    // Fall through
  }

  return false;
}
